import React, { useCallback, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Pagination } from 'swiper/modules';
import { Link } from 'react-router-dom';
import { cardColorClass } from '../../config/design.config';
import SingleCard from '../CardList/SingleCard';
import { useLanguage } from '../../context/LanguageContext';

// Skeleton Card Loader
const CardSkeleton = () => (
    <SwiperSlide>
        <div className="credit-card-box skeleton-card">
            <div
                className="skeleton-loading"
                style={{ width: '100%', height: '200px', borderRadius: '16px' }}
            ></div>
        </div>
    </SwiperSlide>
);

function AllCards({ cards }) {
    const { t } = useLanguage();

    // Check loading and empty states
    const isLoading = !Array.isArray(cards);
    const isEmpty = Array.isArray(cards) && cards.length === 0;
    const hasCards = Array.isArray(cards) && cards.length > 0;

    // Memoize the card handler to prevent unnecessary re-renders
    const handleCardAdd = useCallback(() => {}, []);

    // Memoize the cards rendering
    const renderedCards = useMemo(() => {
        if (isLoading) {
            return Array.from({ length: 2 }).map((_, idx) => (
                <CardSkeleton key={`skeleton-${idx}`} />
            ));
        }

        if (hasCards) {
            return cards.map((card) => (
                <SwiperSlide key={card.id}>
                    <div className={`credit-card-box ${cardColorClass[card?.id % 3]}`}>
                        <SingleCard card={card} onCardAdd={handleCardAdd} />
                    </div>
                </SwiperSlide>
            ));
        }

        return null;
    }, [cards, isLoading, hasCards, handleCardAdd]);

    // Don't render section if no cards and not loading
    if (isEmpty) {
        return null;
    }

    return (
        <section aria-label={t('cards')}>
            <div className="custom-container">
                <div className="title">
                    <h2>{t('cards')}</h2>
                    {hasCards && (
                        <Link
                            to="/cards"
                            className="see-all-link"
                            aria-label={`${t('seeAll')} ${t('cards')}`}
                        >
                            {t('seeAll')}
                        </Link>
                    )}
                </div>

                {isLoading ? (
                    <Swiper
                        spaceBetween={10}
                        slidesPerView={1}
                        loop={false}
                        pagination={{ clickable: true }}
                        modules={[Pagination]}
                        className="card-slider"
                        aria-busy="true"
                        aria-label={t('loadingCards')}
                    >
                        {renderedCards}
                    </Swiper>
                ) : (
                    <Swiper
                        spaceBetween={10}
                        slidesPerView={1}
                        loop={cards.length > 1}
                        pagination={{ clickable: true }}
                        modules={[Pagination]}
                        className="card-slider"
                        aria-label={`${t('cardsSlider')} (${cards.length} ${t('cardsAvailable')})`}
                    >
                        {renderedCards}
                    </Swiper>
                )}
            </div>
        </section>
    );
}

export default AllCards;
